'use client';

import { useState } from 'react';

import {
  TImageTokenWithFile,
  useDeviceImageUploadMutation,
} from '@northladder/apis';
import { CameraCapture } from '@northladder/design-system';
import { useRegionStore } from '@northladder/services';
import {
  IApiError,
  dataURLtoFile,
  ErrorEventsEnum,
  errorLogToRemoteUtil,
} from '@northladder/utilities';

import { useRouteURL } from '../../hooks';

import DynamicImageCropper from '../DynamicImageCropper';

import { ImageUploadError } from './ImageUploadError';
import { ImageUploadIndicator } from './ImageUploadIndicator';

interface ImageUploadContentProps {
  onCloseModal: () => void;
  imageData: TImageTokenWithFile;
  onUpdateImage: (updatedImage: TImageTokenWithFile) => void;
}

/**
 * -----------------------------------------------------------------------------
 * Displays the content of the modal. Switching between the capturing camera &
 * and the upload preview UIs.
 */
export function ImageUploadContent({
  onUpdateImage,
  imageData,
  onCloseModal,
}: ImageUploadContentProps) {
  const { sessionId, assessmentType } = useRouteURL();
  const baseURL = useRegionStore().tenantConfig?.apiBaseURL || '';

  const [previewImageURL, setPreviewImageURL] = useState('');
  const [capturedImgSrc, setCapturedImgSrc] = useState(imageData.croppedImgSrc);
  const [isCroppingMode, setIsCroppingMode] = useState(false);
  // const [previousCrop, setPreviousCrop] = useState<Crop>();

  const handleUploadSuccess = () => {
    setIsCroppingMode(false);
    onCloseModal();

    onUpdateImage({
      ...imageData,
      loadedImgSrc: previewImageURL,
      croppedImgSrc: previewImageURL,
      status: 'completed',
    });
  };

  const handleUploadFailed = (error: IApiError) => {
    // TODO: Handle Upload error case here
    errorLogToRemoteUtil({
      error,
      errorCode: ErrorEventsEnum.ERROR_IN_API_CALL,
      errorTitle: 'Error in Uploading Cosmetic Diag Image',
      message: error.message,
    });
  };

  const {
    error,
    isPending: isUploadingImage,
    uploadImage,
  } = useDeviceImageUploadMutation({
    onSuccessCallback: handleUploadSuccess,
    // TODO: Temp, remove it later.
    onErrorCallback: handleUploadFailed,
  });

  const handleUpdateCapturedImage = (imgURL: string) => {
    setCapturedImgSrc(imgURL);
    setIsCroppingMode(true);
  };

  const handleCancelCrop = () => {
    setIsCroppingMode(false);
    setCapturedImgSrc('');
  };

  const handleUpdatePreviewURL = (croppedImg: string) => {
    setPreviewImageURL(croppedImg);
  };

  // const handleCachePreviewCrop = (previewCrop: Crop) => {
  //   setPreviousCrop(previewCrop);
  // };

  const handleUploadImage = async () => {
    const imagePosition = imageData.metadata.position;
    const imageFormData = new FormData();
    const identifier = imageData.id;
    const time = new Date().getTime().toString();
    const timeStamp = time.slice(-4);

    imageFormData.append('image', capturedImgSrc);

    const imageFile = dataURLtoFile(
      capturedImgSrc,
      `${imagePosition}_${sessionId}_${timeStamp}.jpg`
    );

    // Note: Currently, the apple flow uses a different identifier for the image
    // which is not the case with the GRADE_PRO flow.
    const imageIdentifier =
      assessmentType === 'APPLE' ? imagePosition : identifier;

    uploadImage({
      assessmentType,
      baseURL,
      file: imageFile,
      id: sessionId,
      imageId: imageIdentifier,
    });
  };

  return (
    <div>
      {isUploadingImage ? <ImageUploadIndicator /> : null}
      {error ? (
        <ImageUploadError error={error.message} onRetry={handleUploadImage} />
      ) : null}

      {isCroppingMode && capturedImgSrc ? (
        <DynamicImageCropper
          // previousCrop={previousCrop}
          // onPreviewMode={handleCachePreviewCrop}
          imgSrc={capturedImgSrc}
          onCancelCrop={handleCancelCrop}
          onImageUpload={handleUploadImage}
          onPreviewUpdate={handleUpdatePreviewURL}
        />
      ) : null}

      {!isCroppingMode ? (
        <CameraCapture
          capturedImgSrc={capturedImgSrc}
          onDismissCamera={onCloseModal}
          onUpdateCapturedImage={handleUpdateCapturedImage}
        />
      ) : null}
    </div>
  );
}
